import React, {useState, useCallback, useEffect} from 'react';
import produce from 'immer';
import SMCModal from '../../components/core/SMCModal';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCLabel from '../../components/Simple/SMCLabel';
import SMCTextInput from '../../components/Simple/SMCTextInput';
import {SMCSelect, SMCOption} from '../../components/Simple/SMCSelect';
import SMCButton from '../../components/Simple/SMCButton';
import {validateIpAddress, validateSubnetMask, setProperty} from '../../utils/utils';
import {SystemSettingsGetSchema} from '../../api/DBSchema/SystemSettings';
import {useSMCSnackbar} from '../../utils/hooks';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

const pageSchema = SystemSettingsGetSchema.pages.managementAccessControl.dataListSchema;

function AccessControlEditorModal({
    isOpen = false,
    onCancel,
    onSuccess,
    defaultObject,
    title,
    hasDomainName=false
}) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();

    const [accessControlObject, setAccessControlObject] = useState({});

    const setPageValue = useCallback((param, newValue) => {
        setAccessControlObject(accessControlObj => produce(accessControlObj, draft => (
            setProperty(draft, param, newValue)
        )))
    }, []);

    const type       = accessControlObject[pageSchema.type];
    const ipAddress  = accessControlObject[pageSchema.ipAddr];
    const subnetMask = accessControlObject[pageSchema.subnetMask];
    const domainName = accessControlObject[pageSchema.domainName];
    
    const typeValues = pageSchema.typeValues;

    const onFinish = () => {
        // Validate
        if ((type === typeValues.ipAddress || type === typeValues.network) && !validateIpAddress(ipAddress)) {
            enqueueErrorSnackbar(t('com.sofaware.schema.ATTRIBUTE_INVALID_VALUE_IP', [ipAddress, t('SMP.PLAN.ADMIN_ACCESS_WIZ.IP_ADDRESS')]), {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
            return;
        }
        if (type === typeValues.network && !validateSubnetMask(subnetMask)) {
            enqueueErrorSnackbar(t('com.sofaware.schema.ATTRIBUTE_INVALID_VALUE_SUBNET_MASK', [subnetMask]), {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
            return;
        }
        // Build return object
        let returnObject;
        if (type === typeValues.ipAddress) {
            returnObject = {
                [pageSchema.type]: typeValues.ipAddress,
                [pageSchema.ipAddr]: ipAddress,
                [pageSchema.subnetMask]: null,
                [pageSchema.domainName]: null,
            };
        }
        else if (type === typeValues.network) {
            returnObject = {
                [pageSchema.type]: typeValues.network,
                [pageSchema.ipAddr]: ipAddress,
                [pageSchema.subnetMask]: subnetMask,
                [pageSchema.domainName]: null,
            };
        }
        else if (type === typeValues.domain) {
            returnObject = {
                [pageSchema.type]: typeValues.domain,
                [pageSchema.ipAddr]: null,
                [pageSchema.subnetMask]: null,
                [pageSchema.domainName]: domainName,
            };
        }
        onSuccess(returnObject);
    }

    // On each new open, reset values
    useEffect(() => {
        if (isOpen) {
            if (defaultObject) {
                setAccessControlObject(defaultObject);
            } else {
                const _defaultObject = {
                    [pageSchema.type]: typeValues.ipAddress,
                    [pageSchema.subnetMask]: '',
                    [pageSchema.ipAddr]: ''
                };
                if (hasDomainName) {
                    _defaultObject[pageSchema.domainName] = '';
                }
                setAccessControlObject(_defaultObject);
            }
        }
    }, [isOpen, defaultObject, hasDomainName, typeValues]);

    return (
        <SMCModal
            isOpen={isOpen}
            width={610}
            height={480}
            title={title || t('SMC_GEN_EDIT')}
            onRequestClose={onCancel}
        >
            <SMCModalBody>
                <SMCLabel bold>{t('SMP.PLAN.ADMIN_ACCESS_WIZ.ACCESS_IP_ADDRESS_CONF')}</SMCLabel>
                <SMCSelect
                    value={type}
                    onChange={e => setPageValue(pageSchema.type, e.target.value)}
                    label={t('SMP.PLAN.ADMIN_ACCESS_WIZ.SOURCE_TYPE')}
                >
                    <SMCOption value={typeValues.ipAddress}>{t('SMP.PLAN.ADMIN_ACCESS_WIZ.IP_ADDRESS')}</SMCOption>
                    <SMCOption value={typeValues.network}>{t('SMP.NETWORK')}</SMCOption>
                    { hasDomainName && <SMCOption value={typeValues.domain}>Domain name</SMCOption> }
                </SMCSelect>
                {
                    (type === typeValues.ipAddress || type === typeValues.network) && (
                        <SMCTextInput
                            value={ipAddress}
                            onChange={e => setPageValue(pageSchema.ipAddr, e.target.value)}
                            label={t('SMP.PLAN.ADMIN_ACCESS_WIZ.IP_ADDRESS')}
                        />
                    )
                }
                {
                    type === typeValues.network && (
                        <SMCTextInput
                            value={subnetMask}
                            onChange={e => setPageValue(pageSchema.subnetMask, e.target.value)}
                            label={t('SMP.PLAN.ADMIN_ACCESS_WIZ.SUBNET_MASK')}
                        />
                    )
                }
                {
                    type === typeValues.domain && (
                        <SMCTextInput
                            value={domainName}
                            onChange={e => setPageValue(pageSchema.domainName, e.target.value)}
                            label="Domain name"
                        />
                    )
                }
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={onFinish}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_FINISH')}
                </SMCButton>
                <SMCButton onClick={onCancel}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton> 
            </SMCModalButtonContainer>
        </SMCModal>

    );
}

export default AccessControlEditorModal;